import React, { useEffect, useState, useMemo } from 'react';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { propertiesApi } from '../api/endpoints';
import PropertyDetailsDrawer from '../components/PropertyDetailsDrawer';
import SendBrochureDialog from '../components/SendBrochureDialog';
import { useAuth } from '../context/AuthContext';
import { DeleteButton } from '../components/DeleteButton';
import { CoordinatorDialog } from '../components/CoordinatorDialog';
import { toast } from 'sonner';

// Mapping for status badge styles
const statusBadge = {
  'Ready Possession': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Ready To Build': 'bg-sky-50 text-sky-700 border-sky-200',
  'Under Construction': 'bg-amber-50 text-amber-700 border-amber-200',
  'Launching Soon': 'bg-violet-50 text-violet-700 border-violet-200',
};

// --- Sub-Component: Property Form (Add/Edit) ---
const PropertyFormDialog = ({ onSaved, trigger, initial }) => {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', type: '3BHK Apartments', status: 'Launching Soon', price: '',
    units: 0, sold: 0, location: '', area: '',
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600',
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || '',
        type: initial?.type || '3BHK Apartments',
        status: initial?.status || 'Launching Soon',
        price: initial?.price || '',
        units: initial?.units ?? 0,
        sold: initial?.sold ?? 0,
        location: initial?.location || '',
        area: initial?.area || '',
        image: initial?.image || 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600',
      });
    }
  }, [open, initial]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.location || !form.price) { 
      toast.error('Required fields missing'); 
      return; 
    }
    setSaving(true);
    try {
      const payload = { ...form, units: Number(form.units) || 0, sold: Number(form.sold) || 0 };
      const saved = isEdit ? await propertiesApi.update(initial.id, payload) : await propertiesApi.create(payload);
      onSaved(saved, isEdit);
      toast.success(isEdit ? 'Updated Successfully' : 'Added Successfully');
      setOpen(false);
    } catch (e) {
      toast.error('Failed to save project');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Project' : 'Add Project'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2">
            <Label>Project Name</Label>
            <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="e.g. Skyline Residency" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={v => update('type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['3BHK Apartments','4 BHKD','Bungalow Plots','Residential Plots','Luxury Plots'].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={v => update('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Ready Possession','Ready To Build','Under Construction','Launching Soon'].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Location</Label>
            <Input value={form.location} onChange={e => update('location', e.target.value)} placeholder="City, Area" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand-navy">
            {saving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Sub-Component: Brochure upload button (hidden file input) ---
const BrochureUploadButton = ({ property, onSaved, compact = false }) => {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Brochure must be a PDF file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const updated = await propertiesApi.uploadBrochure(property.id, fd);
      onSaved(updated, true);
      toast.success('Brochure uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload brochure');
    } finally { setUploading(false); }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} data-testid={`brochure-file-input-${property.id}`} />
      {compact ? (
        <Button
          size="icon" variant="outline" className="h-9 w-9 border-slate-200 shrink-0"
          title="Replace brochure PDF" disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid={`brochure-replace-${property.id}`}
        >
          {uploading ? <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icons.Upload className="w-3.5 h-3.5" />}
        </Button>
      ) : (
        <Button
          variant="outline" className="flex-1 text-xs h-9 border-dashed border-slate-300 text-slate-600"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid={`brochure-upload-${property.id}`}
        >
          {uploading
            ? <><Icons.Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading…</>
            : <><Icons.FileUp className="w-3 h-3 mr-1" /> Upload Brochure PDF</>}
        </Button>
      )}
    </>
  );
};

// --- Main Component ---
const Properties = () => {
  const { hasPermission, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canCreate = hasPermission('properties', 'create');
  const canEdit = hasPermission('properties', 'edit');
  const canDelete = hasPermission('properties', 'delete');

  const [data, setData] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('all');
  const [detailsId, setDetailsId] = useState(null);

  // Fetch properties and assignments
  useEffect(() => {
    (async () => {
      try {
        const [props, assignData] = await Promise.all([
          propertiesApi.list(),
          propertiesApi.getAssignments?.() || []
        ]);
        setData(props);
        setAssignments(assignData);
      } catch (e) {
        console.error("Data fetch error:", e);
        toast.error("Failed to load project data");
      } finally { setLoading(false); }
    })();
  }, []);

  // Helper: Find agent for a specific property
  const getAgentForProperty = (propName) => {
    const assignment = assignments.find(a => a.projectName === propName);
    return assignment ? assignment.employeeName : null;
  };

  // Memoized filtering logic
  const filtered = useMemo(() => {
    return data.filter(p => {
      const matchQ = (p.name + p.location).toLowerCase().includes(q.toLowerCase());
      const matchT = tab === 'all' || 
        (tab === 'ready' && p.status === 'Ready Possession') ||
        (tab === 'plots' && p.type.toLowerCase().includes('plots'));
      return matchQ && matchT;
    });
  }, [data, q, tab]);

  const onSaved = (saved, isEdit) => {
    setData(prev => isEdit ? prev.map(x => x.id === saved.id ? saved : x) : [saved, ...prev]);
  };

  const onDelete = async (p) => {
    try {
      await propertiesApi.remove(p.id);
      setData(d => d.filter(x => x.id !== p.id));
      toast.success("Project removed successfully");
    } catch (e) {
      toast.error("Failed to delete project");
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header & Filter Bar */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex flex-1 items-center gap-4 w-full">
            <div className="relative flex-1 max-w-md">
              <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                className="pl-9" 
                placeholder="Search projects by name or location..." 
                value={q} 
                onChange={e => setQ(e.target.value)} 
              />
            </div>
            
            <Tabs value={tab} onValueChange={setTab} className="hidden lg:block">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="ready">Ready Possession</TabsTrigger>
                <TabsTrigger value="plots">Plots</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            {/* Mobile Tab Switcher */}
            <div className="lg:hidden flex-1">
              <Select value={tab} onValueChange={setTab}>
                <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  <SelectItem value="ready">Ready Possession</SelectItem>
                  <SelectItem value="plots">Plots</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {canCreate && (
              <PropertyFormDialog 
                onSaved={onSaved} 
                trigger={
                  <Button className="bg-brand-navy text-white hover:bg-slate-800 shrink-0">
                    <Icons.Plus className="w-4 h-4 mr-2" /> Add Project
                  </Button>
                } 
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Property Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-80 w-full rounded-xl" />)
        ) : filtered.length > 0 ? (
          filtered.map(p => {
            const agent = getAgentForProperty(p.name);
            return (
              <Card key={p.id} className="overflow-hidden group hover:shadow-lg transition-all border-slate-200">
                {/* Image Section */}
                <div className="relative h-44">
                  <img src={p.image} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt={p.name} />
                  <Badge className={`absolute top-3 left-3 shadow-sm ${statusBadge[p.status] || 'bg-slate-100'}`}>
                    {p.status}
                  </Badge>
                  
                  {/* Actions Overlay */}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canEdit && (
                      <PropertyFormDialog 
                        initial={p} 
                        onSaved={onSaved} 
                        trigger={<Button size="icon" variant="secondary" className="h-8 w-8 shadow-md"><Icons.Pencil className="w-3 h-3"/></Button>} 
                      />
                    )}
                    {canDelete && <DeleteButton onConfirm={() => onDelete(p)} />}
                  </div>

                  {/* Agent Tag Overlay */}
                  {agent && (
                    <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur shadow-sm px-2 py-1 rounded-md text-[10px] font-bold flex items-center text-slate-700">
                      <Icons.UserCheck className="w-3 h-3 mr-1 text-emerald-600" />
                      {agent.toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <CardContent className="p-4">
                  <div className="mb-4">
                    <h3 className="font-bold text-lg text-slate-900 truncate">{p.name}</h3>
                    <p className="text-slate-500 text-sm flex items-center">
                      <Icons.MapPin className="w-3 h-3 mr-1 text-slate-400"/> {p.location}
                    </p>
                  </div>

                  {/* Agent Display Area */}
                  <div className="flex items-center justify-between p-3 mb-4 rounded-lg bg-slate-50 border border-slate-100">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Assigned Agent</span>
                    <span className="text-xs font-semibold text-slate-700">
                      {agent || <span className="text-slate-300 font-normal italic">None</span>}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <CoordinatorDialog 
                      property={p} 
                      trigger={
                        <Button variant="outline" className="flex-1 text-xs h-9 border-slate-200">
                          <Icons.UserPlus className="w-3 h-3 mr-1"/> {agent ? 'Reassign' : 'Assign'}
                        </Button>
                      } 
                    />
                    <Button
                      className="flex-1 text-xs bg-brand-navy h-9"
                      data-testid={`view-details-${p.id}`}
                      onClick={() => setDetailsId(p.id)}
                    >View Details</Button>
                  </div>

                  {/* Brochure row */}
                  <div className="flex gap-2 mt-2">
                    {p.brochure_url ? (
                      <>
                        <a
                          href={p.brochure_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${p.brochure_url}` : p.brochure_url}
                          target="_blank" rel="noopener noreferrer" className="flex-1"
                        >
                          <Button variant="outline" className="w-full text-xs h-9 border-slate-200" data-testid={`brochure-view-${p.id}`}>
                            <Icons.FileText className="w-3 h-3 mr-1 text-rose-500" /> Brochure
                          </Button>
                        </a>
                        <SendBrochureDialog
                          project={p}
                          trigger={
                            <Button className="flex-1 text-xs h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`brochure-whatsapp-${p.id}`}>
                              <Icons.MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                            </Button>
                          }
                        />
                        {canEdit && <BrochureUploadButton property={p} onSaved={onSaved} compact />}
                      </>
                    ) : canEdit ? (
                      <BrochureUploadButton property={p} onSaved={onSaved} />
                    ) : (
                      <div className="flex-1 text-[11px] text-slate-400 italic text-center py-2 border border-dashed border-slate-200 rounded-md">
                        No brochure uploaded
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <div className="col-span-full py-20 text-center border-2 border-dashed border-slate-200 rounded-2xl">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-50 mb-4">
              <Icons.SearchX className="w-6 h-6 text-slate-300" />
            </div>
            <h3 className="text-slate-900 font-medium">No projects found</h3>
            <p className="text-slate-500 text-sm">Try adjusting your search or filters.</p>
          </div>
        )}
      </div>

      {/* ADMIN VIEW: Assignment Table */}
      {isAdmin && (
        <Card className="border-slate-200 overflow-hidden shadow-sm">
          <div className="bg-slate-50/80 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
              <Icons.ShieldCheck className="w-4 h-4 text-brand-navy" />
              Administrative Overview: Project Assignments
            </h3>
            <Badge variant="secondary" className="bg-brand-navy/10 text-brand-navy border-none">Admin Only</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50/50 text-slate-400 uppercase text-[10px] tracking-widest font-bold">
                <tr>
                  <th className="px-6 py-4">Project Name</th>
                  <th className="px-6 py-4">Assigned Professional</th>
                  <th className="px-6 py-4">Current Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {assignments.length === 0 ? (
                  <tr><td colSpan="4" className="px-6 py-12 text-center text-slate-400">No active assignments found in the system.</td></tr>
                ) : assignments.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50/40 transition-colors">
                    <td className="px-6 py-4 font-semibold text-slate-700">{a.projectName}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-bold text-brand-navy">
                          {a.employeeName?.substring(0, 2).toUpperCase()}
                        </div>
                        <span className="text-slate-600">{a.employeeName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-600 border-emerald-100">Active</Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button variant="ghost" size="sm" className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 h-8">
                        Revoke Access
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <PropertyDetailsDrawer
        propertyId={detailsId}
        open={!!detailsId}
        onOpenChange={(v) => !v && setDetailsId(null)}
      />
    </div>
  );
};

export default Properties;